import { useState, useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { PageHeader } from "@/layouts/PageHeader";
import { LucideUsers, LucideLogOut } from "lucide-react";
import ReportFolderList from "@/components/entities/ReportFolderList";
import { EmptyState } from "@/components/ui/EmptyState";

export default function SharedReportFolders() {
  const { getToken } = useAuth();

  const [sharedReportFolders, setSharedReportFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const fetchSharedReportFolders = async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${BACKEND_URL}api/shared-report-folders`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error("Failed to fetch shared Report Folders");
        setSharedReportFolders(await res.json());
        setFetchError(null);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching shared Report Folders:", err);
        setFetchError(
          "Could not load shared Report Folders. Please refresh the page."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchSharedReportFolders();
    return () => controller.abort();
  }, [getToken]);

  const handleLeave = async (item) => {
    if (
      !confirm(
        `Leave "${item.name}"? You will lose access to this shared Report Folder.`
      )
    )
      return;
    try {
      const token = await getToken();
      await fetch(`${BACKEND_URL}api/shared-report-folders/${item._id}/leave`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setSharedReportFolders((prev) => prev.filter((c) => c._id !== item._id));
    } catch (err) {
      console.error("Error leaving shared Report Folder:", err);
      toast.error("Failed to leave Report Folder. Please try again.");
    }
  };

  return (
    <div className="px-10 py-9 max-w-[960px] mx-auto max-md:px-4 max-md:pt-5 max-md:pb-6">
      <PageHeader
        title="Shared Report Folders"
        subtitle="Report Folders that other teachers have shared with you."
      />

      {fetchError && (
        <div className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {fetchError}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[60px] bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : sharedReportFolders.length === 0 ? (
        <EmptyState
          icon={LucideUsers}
          title="No shared Report Folders yet"
          description="When another teacher shares a Report Folder with you, it will appear here."
        />
      ) : (
        <ReportFolderList
          items={sharedReportFolders}
          getLinkPath={(item) => `/shared-report-folders/${item._id}`}
          getOwnerName={(item) => item.sharedBy?.name ? item.sharedBy.name : undefined}
          getPermission={(item) => item.permission}
          getMenuItems={(item) => [
            { label: "Leave Report Folder", icon: LucideLogOut, onClick: () => handleLeave(item), destructive: true },
          ]}
        />
      )}
    </div>
  );
}
